import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GitPullRequestArrow, Loader2, ShieldAlert } from "lucide-react";

/* Amendment resolution PROGRESS banner (deal header). Shows "N of M amendments addressed — price firms at
   100%" whenever material amendments still block the firm price (SF-1442 Block 17 stays blank). Updates in
   real time as amendments are addressed — it listens to the shared `ironclad-refresh` event the monitor chip
   dispatches — and self-hides the instant every material amendment is addressed, so the price can firm and
   Block 17 populates. */
export function AmendmentProgressBanner({ dealId, reload }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/bids/${dealId}/amendments`).then((r) => setData(r.data)).catch(() => {});
  }, [dealId]);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("ironclad-refresh", h);
    return () => window.removeEventListener("ironclad-refresh", h);
  }, [load]);

  if (!data) return null;
  const material = (data.amendments || []).filter((a) => a.impact?.material);
  const total = material.length;
  if (!total) return null;                       // no material amendments — no pricing block to resolve
  const addressed = material.filter((a) => a.impact?.addressed).length;
  const outstanding = total - addressed;
  if (outstanding <= 0) return null;             // all addressed — banner clears, price firms, Block 17 fills

  const pct = Math.round((addressed / total) * 100);

  const runMonitor = async () => {
    setBusy(true);
    const tid = toast.loading("Running amendment monitor — parsing, flagging & re-grounding affected lines…");
    try {
      const r = await api.post(`/bids/${dealId}/amendments/monitor`);
      const m = r.data?.monitor || {};
      toast.success(`Amendment monitor v${m.version ?? "?"} — re-grounding complete.`, { id: tid });
      load();
      reload && reload();
      window.dispatchEvent(new CustomEvent("ironclad-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the amendment monitor.", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="amendment-progress-banner" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldAlert className="h-4 w-4 text-amber-700 shrink-0" />
            <span className="text-sm font-semibold text-amber-900" data-testid="amendment-progress-count">
              {addressed} of {total} amendments addressed
            </span>
            <span className="text-[11px] font-medium text-amber-700">— price firms at 100%</span>
          </div>
          <p className="text-[12px] text-amber-800 mt-1">
            {outstanding} amendment{outstanding === 1 ? "" : "s"} still block{outstanding === 1 ? "s" : ""} the firm
            price — SF-1442 Block 17 stays blank until every material amendment is re-shredded, re-priced &amp;
            addressed.
          </p>
        </div>
        <button onClick={runMonitor} disabled={busy} data-testid="amendment-progress-run-btn"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50 shrink-0">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitPullRequestArrow className="h-3.5 w-3.5" />}
          Run monitor / re-ground all
        </button>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-amber-200 overflow-hidden" data-testid="amendment-progress-bar">
        <div className="h-full bg-amber-500 transition-[width] duration-500 ease-out rounded-full"
             style={{ width: `${pct}%` }} data-testid="amendment-progress-bar-fill" />
      </div>
      <div className="mt-1 text-right text-[11px] font-semibold text-amber-700">{pct}%</div>
    </div>
  );
}
